import { Box, ListItemButton, ListItemIcon, ListItemText, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";

import { AppNotification } from "@interfaces/notification";
import { useMarkReadMutation } from "@services/api/notifications";

import { resolveEntityRoute } from "./entityRoutes";
import { formatRelative } from "./formatRelative";
import { iconFor } from "./typeIcons";

export interface NotificationItemProps {
    notification: AppNotification;
    onNavigate: () => void;
}

export const NotificationItem = ({ notification: n, onNavigate }: NotificationItemProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [markRead] = useMarkReadMutation();

    const Icon = iconFor(n.type_code, n.category);
    const isUnread = !n.read_at;

    const handleClick = () => {
        if (isUnread) markRead(n.id);

        const href = resolveEntityRoute(n.entity_type, n.entity_id, n.payload, location.pathname);

        if (href) navigate(href);

        onNavigate();
    };

    return (
        <ListItemButton
            onClick={handleClick}
            sx={{
                alignItems: "flex-start",
                bgcolor: isUnread ? "action.hover" : "transparent",
            }}
        >
            <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                <Icon size={20} />
            </ListItemIcon>

            <ListItemText
                primary={n.title}
                secondary={
                    <>
                        {n.body}
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                            {formatRelative(n.created_at)}
                        </Typography>
                    </>
                }
                slotProps={{
                    primary: { variant: "body2", sx: { fontWeight: isUnread ? 600 : 400 } },
                    secondary: { component: "div", variant: "body2" },
                }}
            />

            {isUnread && (
                <Box
                    sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: "primary.main",
                        mt: 1,
                        ml: 1,
                        flexShrink: 0,
                    }}
                />
            )}
        </ListItemButton>
    );
};
